import React, { useEffect, useState } from 'react'
import Styles from './Login.module.css'
import Link from '@mui/material/Link'
import { IFButton, IFText, IFTextInput } from 'Components'
import { Colors, Images } from 'Theme'
import AuthSelectors from 'Stores/Auth/Selectors'
import AuthActions from 'Stores/Auth/Actions'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import validator from 'validator'
import ReactPinField from 'react-pin-field'
import { styled as muiStyled } from '@mui/system'
import QRCode from 'qrcode'
import { useNavigate } from 'react-router-dom'

const StyledPinField = muiStyled(ReactPinField)(({ theme }) => ({
  backgroundColor: `${Colors.pinFieldBackground}`,
  border: `1px solid ${Colors.pinFieldBorder}`,
  borderRadius: '0.3rem',
  fontSize: '2rem',
  margin: '0.25rem',
  height: '52px',
  outline: 'none',
  textAlign: 'center',
  transitionDuration: '250ms',
  transitionProperty: 'background, color, border, box-shadow, transform',
  width: '42px',
  '&:focus': {
    borderColor: `${Colors.primary}`,
    outline: 'none',
    transform: 'scale(1.05)',
  },
  '&:invalid': {
    animation: 'shake 3 linear 75ms',
    borderColor: `${Colors.pinFieldInvalidBorder}`,
    boxShadow: `0 0 0.25rem ${Colors.pinFieldInvalidBoxShadow}`,
  },
}))

const LoginForm = ({ login, isLoginLoading }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState()
  const [passwordError, setPasswordError] = useState()
  const [canLogin, setCanLogin] = useState(false)

  const validateLoginState = (emailValue, passwordValue) => {
    const isValid =
      validator.isEmail(emailValue) &&
      validator.isLength(passwordValue, { min: 8, max: 100 })

    setCanLogin(isValid)
  }

  return (
    <>
      <div className={Styles.inputContainer}>
        <IFText className={Styles.label}>{t('Login.EmailAddress')}</IFText>

        <IFTextInput
          placeholder={t('Login.EmailAddressPlaceholder')}
          style={{ caretColor: Colors.primary }}
          className={Styles.input}
          isFixed
          value={email}
          type="email"
          name="email"
          onChange={(e) => {
            const value = e.target.value
            setEmail(value)

            if (emailError) {
              validator.isEmail(value)
                ? setEmailError(undefined)
                : setEmailError(t('Login.EmailError'))
            }

            validateLoginState(value, password)
          }}
          onBlur={() => {
            validator.isEmail(email)
              ? setEmailError(undefined)
              : setEmailError(t('Login.EmailError'))

            validateLoginState(email, password)
          }}
          onKeyPress={(event) => {
            if (
              (event.code === 'Enter' || event.code === 'NumpadEnter') &&
              canLogin
            ) {
              login(email, password)
            }
          }}
          {...(emailError ? { errorText: emailError } : {})}
        />

        <div style={{ height: '1.75rem' }} />
        <IFText className={Styles.label}>{t('Login.Password')}</IFText>
        <IFTextInput
          className={Styles.input}
          style={{ caretColor: Colors.primary }}
          isFixed
          value={password}
          type="password"
          onChange={(e) => {
            const value = e.target.value
            setPassword(value)

            if (passwordError) {
              validator.isLength(value, { min: 8, max: 100 })
                ? setPasswordError(undefined)
                : setPasswordError(t('Login.PasswordError'))
            }

            validateLoginState(email, value)
          }}
          onBlur={() => {
            validator.isLength(password, { min: 8, max: 100 })
              ? setPasswordError(undefined)
              : setPasswordError(t('Login.PasswordError'))

            validateLoginState(email, password)
          }}
          onKeyPress={(event) => {
            if (
              (event.code === 'Enter' || event.code === 'NumpadEnter') &&
              canLogin
            ) {
              login(email, password)
            }
          }}
          {...(passwordError ? { errorText: passwordError } : {})}
        />
      </div>
      <div className={Styles.signIn}>
        <IFButton
          text={t('Login.SignIn')}
          color={Colors.primary}
          isFill
          size="sm"
          onClick={() => login(email, password)}
          isLoading={isLoginLoading}
          isDead={!canLogin}
        />
      </div>
      <div className={Styles.linkContainer}>
        <Link
          href="/auth/reset"
          variant="body2"
          className={Styles.link}
          style={{ color: Colors.primary }}
        >
          <IFText className={Styles.link}>{t('Login.ForgotPassword')}</IFText>
        </Link>
      </div>
    </>
  )
}

const TwoFactorAuth = ({ onTwoFactorVerify, isLoading, challengeId }) => {
  const { t } = useTranslation()
  const [pin, setPin] = useState('')

  const isValid = pin.length === 6

  return (
    <div className={Styles.twoFAContainer}>
      <IFText className={Styles.title}>{t('Login.TwoFactorTitle')}</IFText>
      <IFText>{t('Login.TwoFactorDescription')}</IFText>
      <div className={Styles.PinFieldContainer}>
        <StyledPinField
          length={6}
          validate={t('ResetPassword.PinValidationString')}
          onChange={(value) => setPin(value)}
        />
      </div>
      <div className={Styles.twoFASignIn}>
        <IFButton
          text={t('Login.Verify')}
          color={Colors.primary}
          isFill
          size="sm"
          onClick={() => onTwoFactorVerify(pin, challengeId)}
          isDead={!isValid}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

const TwoFactorSetup = ({
  otpauthUrl,
  manualKey,
  challengeId,
  onTwoFactorSetupVerify,
  isLoading,
}) => {
  const { t } = useTranslation()
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [continueSetup, setContinueSetup] = useState(false)

  useEffect(() => {
    if (otpauthUrl) {
      QRCode.toDataURL(otpauthUrl)
        .then((url) => {
          setQrCodeUrl(url)
        })
        .catch((err) => {})
    }
  }, [otpauthUrl])

  return (
    <>
      {continueSetup ? (
        <TwoFactorAuth
          challengeId={challengeId}
          isLoading={isLoading}
          onTwoFactorVerify={onTwoFactorSetupVerify}
        />
      ) : (
        <>
          <div className={Styles.twoFASetupContainer}>
            <div className={Styles.twoFATitleContainer}>
              <IFText className={Styles.twoFATitle}>
                {t('Login.SetupTwoFactorTitle')}
              </IFText>

              <IFText
                className={Styles.description}
                style={{
                  color: Colors.gray,
                }}
              >
                {t('Login.SetupTwoFactorDescription')}
              </IFText>
            </div>

            {/* Step 1 */}
            <div className={Styles.stepContainer}>
              <div className={Styles.stepHeader}>
                <div
                  className={Styles.stepCircle}
                  style={{
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                  }}
                >
                  1
                </div>
                <div className={Styles.stepTextContainer}>
                  <IFText className={Styles.stepTitle}>
                    {t('Login.DownloadAuthenticator')}
                  </IFText>
                  <IFText
                    className={Styles.stepDescription}
                    style={{
                      color: Colors.gray,
                    }}
                  >
                    {t('Login.DownloadAuthenticatorDescription')}
                  </IFText>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className={Styles.stepContainer}>
              <div className={Styles.stepHeader}>
                <div
                  className={Styles.stepCircle}
                  style={{
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                  }}
                >
                  2
                </div>
                <div className={Styles.stepTextContainer}>
                  <IFText className={Styles.stepTitle}>
                    {t('Login.ScanBarcode')}
                  </IFText>
                  <IFText
                    style={{
                      color: Colors.gray,
                    }}
                  >
                    {t('Login.ScanBarcodeDescription')}
                  </IFText>
                  <div className={Styles.qrRow}>
                    <div className={Styles.qrContainer}>
                      {qrCodeUrl && <img src={qrCodeUrl} alt="2FA QR Code" />}
                    </div>

                    <div className={Styles.manualKeyContainer}>
                      <IFText
                        className={Styles.manualKeyLabel}
                        style={{
                          color: Colors.gray,
                        }}
                      >
                        {t('Login.ManualKeyHint')}
                      </IFText>

                      <div
                        className={Styles.manualKeyBox}
                        style={{ backgroundColor: Colors.grey }}
                      >
                        <IFText className={Styles.manualKeyValue}>
                          {manualKey}
                        </IFText>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={Styles.twoFASetupSignIn}>
              <IFButton
                text={t('Login.Continue')}
                color={Colors.primary}
                isFill
                size="sm"
                onClick={() => setContinueSetup(true)}
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}

const Login = ({
  login,
  onTwoFactorVerify,
  isLoginLoading,
  operatorUserLoginResetClientState,
  requires2FA,
  requires2FASetup,
  challengeId,
  otpauthUrl,
  manualKey,
  onTwoFactorSetupVerify,
  setupComplete,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    operatorUserLoginResetClientState()
  }, [])

  useEffect(() => {
    if (setupComplete) {
      navigate('/auth/login')
    }
  }, [setupComplete])

  return (
    <div>
      <div
        className={Styles.backgroundImage}
        style={{
          backgroundImage: `url(${Images.loginBackground})`,
        }}
      />
      <div
        className={Styles.loginwrapper}
        style={{
          backgroundColor: Colors.loginBackgroundColor,
          boxShadow: `0px 0px 4px ${Colors.loginBackgroundShadow}`,
        }}
      >
        <div className={Styles.logo}>
          <img className={Styles.img} src={Images.infinity} alt="logo" />
        </div>
        {requires2FASetup || requires2FA ? null : (
          <div className={Styles.titleContainer}>
            <IFText className={Styles.title}> {t('Login.Welcome')}</IFText>
          </div>
        )}
        {requires2FA ? (
          <TwoFactorAuth
            isLoading={isLoginLoading}
            challengeId={challengeId}
            onTwoFactorVerify={onTwoFactorVerify}
          />
        ) : requires2FASetup ? (
          <TwoFactorSetup
            challengeId={challengeId}
            otpauthUrl={otpauthUrl}
            manualKey={manualKey}
            onTwoFactorSetupVerify={onTwoFactorSetupVerify}
            isLoading={isLoginLoading}
          />
        ) : (
          <LoginForm login={login} isLoginLoading={isLoginLoading} />
        )}
      </div>
    </div>
  )
}
function mapDispatchToProps(dispatch) {
  return {
    login: (email, password) =>
      dispatch(AuthActions.operatorUserLogin(email, password)),
    operatorUserLoginResetClientState: () =>
      dispatch(AuthActions.operatorUserLoginResetClientState()),
    onTwoFactorVerify: (token, challengeId) =>
      dispatch(AuthActions.onTwoFactorVerify(token, challengeId)),
    onTwoFactorSetupVerify: (token, challengeId) =>
      dispatch(AuthActions.onTwoFactorSetupVerify(token, challengeId)),
  }
}

const mapStateToProps = (state) => ({
  isLoginLoading: AuthSelectors.isOperatorUserLoginLoading(state),
  requires2FA: AuthSelectors.getRequires2FA(state),
  requires2FASetup: AuthSelectors.getRequires2FASetup(state),
  challengeId: AuthSelectors.getChallengeId(state),
  otpauthUrl: AuthSelectors.getOtpauthUrl(state),
  manualKey: AuthSelectors.getManualKey(state),
  setupComplete: AuthSelectors.getSetupComplete(state),
})

Login.propTypes = {
  login: PropTypes.func,
  isLoginLoading: PropTypes.bool,
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
